import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useUIStore } from '../../stores/useUIStore';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useShopStore } from '../../stores/useShopStore';

/* ------------------------------------------------------------------
   Precios oficiales y cálculo
   ------------------------------------------------------------------ */
const OUR: Record<string, number> = { ARS: 7499, USD: 6.5, MXN: 92 };
const EPIC: Record<string, number> = { ARS: 14400, USD: 8.99, MXN: 159 };
const SYM: Record<string, string> = { ARS: '$', USD: 'US$', MXN: '$' };

const TIERS = [
  { v: 800, b: 0, p: { ARS: 14400, MXN: 159, USD: 7.19 } },
  { v: 2400, b: 5, p: { ARS: 43200, MXN: 399, USD: 18.05 } },
  { v: 4500, b: 10, p: { ARS: 81000, MXN: 649, USD: 29.35 } },
  { v: 12500, b: 15, p: { ARS: 225000, MXN: 1629, USD: 73.65 } },
];

const fmt = (n: number, c: string) =>
  c === 'USD' ? n.toFixed(2) : Math.round(n).toLocaleString('es-AR');
const our = (v: number, c: string) => (v / 1000) * (OUR[c] || 7499);
const epic = (v: number, c: string) => (v / 800) * (EPIC[c] || 14400);

interface ShopSkin {
  id: string;
  name: string;
  image: string;
  vbucks: number;
  section: string;
}

const DEF_SKINS: ShopSkin[] = [
  { id: 'foco', name: 'Foco campeona', vbucks: 1000, section: 'Originales de Fortnite', image: 'https://fortnite-api.com/images/cosmetics/br/character_cyberfncs/featured.png' },
  { id: 'speed', name: 'ISuperSpeed', vbucks: 1200, section: 'Originales de Fortnite', image: 'https://fortnite-api.com/images/cosmetics/br/character_honorbraceleap/featured.png' },
  { id: 'comando', name: 'Comando', vbucks: 800, section: 'Originales de Fortnite', image: 'https://fortnite-api.com/images/cosmetics/br/cid_016_athena_commando_f/featured.png' },
  { id: 'iron', name: 'Ironmouse Decora', vbucks: 1500, section: 'Destacados', image: 'https://fortnite-api.com/images/cosmetics/br/character_feelunionmoo/featured.png' },
  { id: 'johnny', name: 'Johnny Silverhand', vbucks: 1500, section: 'Destacados', image: 'https://fortnite-api.com/images/cosmetics/br/character_mustardtoast/featured.png' },
  { id: 'calipso', name: 'Calipso', vbucks: 800, section: 'Originales de Fortnite', image: 'https://fortnite-api.com/images/cosmetics/br/character_bluemonday/featured.png' },
  { id: 'baymax', name: 'Baymax héroe', vbucks: 1500, section: 'Destacados', image: 'https://fortnite-api.com/images/cosmetics/br/character_oysterknock/featured.png' },
];

function extractSkins(j: any): ShopSkin[] | null {
  const es = j?.data?.entries || j?.entries || (Array.isArray(j) ? j : null);
  if (!Array.isArray(es)) return null;
  const seen = new Set<string>(), secs = new Set<string>(), out: ShopSkin[] = [];
  for (const strict of [true, false]) {
    for (const e of es) {
      if (out.length === 7) break;
      if (e.bundle || !e.layout || (e.layoutId || '').toLowerCase().startsWith('alc')) continue;
      const sec = e.layout.name || e.layout.category;
      if (strict && secs.has(sec)) continue;
      const o = (e.brItems || []).find((i: any) => i.type && i.type.value === 'outfit');
      const img = o?.images?.featured || o?.images?.large;
      if (!img || seen.has(o.id)) continue;
      seen.add(o.id);
      secs.add(sec);
      out.push({
        id: o.id,
        name: o.name,
        image: img,
        vbucks: e.finalPrice || e.regularPrice || 1200,
        section: sec || 'Destacados',
      });
    }
  }
  return out.length === 7 ? out : null;
}

const secsLeft = () => {
  const n = new Date();
  return Math.max(0, Math.floor((Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate() + 1) - n.getTime()) / 1000));
};

/* ------------------------------------------------------------------
   Countdown Timer oficial
   ------------------------------------------------------------------ */
function Countdown({ onRollover }: { onRollover?: () => void }) {
  const [left, setLeft] = useState(secsLeft);
  useEffect(() => {
    const t = setInterval(() => {
      const s = secsLeft();
      setLeft(s);
      if (s === 0 && onRollover) setTimeout(onRollover, 3000);
    }, 1000);
    return () => clearInterval(t);
  }, [onRollover]);
  const tm = [Math.floor(left / 3600), Math.floor((left / 60) % 60), left % 60]
    .map((v) => String(v).padStart(2, '0'))
    .join(':');

  return (
    <span className="tm" role="timer" title="Tiempo hasta que rote la tienda" aria-label={`La tienda rota en ${tm}`}>
      <i className="fa-regular fa-clock" aria-hidden="true"></i>
      <span className="tm-lbl" aria-hidden="true">Rota en</span>
      <span className="tm-val" aria-hidden="true">{tm}</span>
    </span>
  );
}

/* ------------------------------------------------------------------
   SkinCard memoizada
   ------------------------------------------------------------------ */
const SkinCard = memo(function SkinCard({
  s,
  cur,
  hidden,
  eager,
  onCardClick,
}: {
  s: ShopSkin;
  cur: string;
  hidden?: boolean;
  eager?: boolean;
  onCardClick?: (s: ShopSkin) => void;
}) {
  const o = our(s.vbucks, cur);
  const e = epic(s.vbucks, cur);
  const discountPct = Math.round((1 - o / e) * 100);

  return (
    <article
      className="sk"
      aria-hidden={hidden ? true : undefined}
      onClick={() => onCardClick && onCardClick(s)}
      style={{ cursor: 'pointer' }}
    >
      <div className="pic">
        <img
          src={s.image}
          alt={hidden ? '' : s.name}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
        />
        <span className="off">-{discountPct}%</span>
      </div>
      <div className="inf">
        <h3 title={s.name}>
          <span>{s.name}</span>
        </h3>
        <p className="vb">
          <i aria-hidden="true">V</i>
          {s.vbucks.toLocaleString('es-AR')} Pavos
        </p>
        <div className="pr">
          <span>Epic Games</span>
          <span>{SYM[cur]} {fmt(e, cur)}</span>
        </div>
        <div className="pr us">
          <span>Nuestro precio</span>
          <b>{SYM[cur]} {fmt(o, cur)}</b>
        </div>
      </div>
    </article>
  );
});

/* ------------------------------------------------------------------
   Carrusel 1:1 con Swipe, Touch, Dots animados y peeking
   ------------------------------------------------------------------ */
function ShopCarousel({ cur, onSelectSkin }: { cur: string; onSelectSkin: (s: ShopSkin) => void }) {
  const [skins, setSkins] = useState<ShopSkin[]>(() => {
    try {
      const storeEntries = useShopStore.getState().rawEntries;
      if (storeEntries && storeEntries.length > 0) {
        const fromStore = extractSkins(storeEntries);
        if (fromStore) return fromStore;
      }
      const rawCached = localStorage.getItem('gf_shop_raw_cache_v2');
      if (rawCached) {
        const parsed = JSON.parse(rawCached);
        const fromRaw = extractSkins(parsed?.entries || parsed);
        if (fromRaw) return fromRaw;
      }
      const cached = localStorage.getItem('fortnite_shop_cache_v2');
      return (cached && extractSkins(JSON.parse(cached))) || DEF_SKINS;
    } catch {
      return DEF_SKINS;
    }
  });

  const n = skins.length;
  const [idx, setIdx] = useState(n);
  const [anim, setAnim] = useState(true);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [stepPx, setStepPx] = useState(240);

  const shopRef = useRef<HTMLDivElement>(null);
  const trkRef = useRef<HTMLDivElement>(null);
  const idxRef = useRef(n);
  const busy = useRef(false);
  const sx = useRef<number | null>(null);
  const dragDistance = useRef(0);
  const hold = useRef({ hover: false, focus: false });
  const visibleRef = useRef(true);
  idxRef.current = idx;

  const loop = [...skins, ...skins, ...skins];

  const measureStep = useCallback(() => {
    const el = trkRef.current;
    if (!el || el.children.length < 2) return;
    const diff = (el.children[1] as HTMLElement).offsetLeft - (el.children[0] as HTMLElement).offsetLeft;
    if (diff > 50) setStepPx(diff);
  }, []);

  useEffect(() => {
    measureStep();
    window.addEventListener('resize', measureStep);
    return () => window.removeEventListener('resize', measureStep);
  }, [measureStep, skins]);

  useEffect(() => {
    const el = shopRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((es) => {
      visibleRef.current = es[0].isIntersecting;
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const load = useCallback(() => {
    const storeEntries = useShopStore.getState().rawEntries;
    if (storeEntries && storeEntries.length > 0) {
      const fromStore = extractSkins(storeEntries);
      if (fromStore) {
        setSkins(fromStore);
        return;
      }
    }

    fetch('/api/shop/raw')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const x = data ? extractSkins(data) : null;
        if (x) {
          setSkins(x);
          try {
            localStorage.setItem('fortnite_shop_cache_v2', JSON.stringify(data));
          } catch {}
          return;
        }
        return fetch('https://fortnite-api.com/v2/shop?language=es')
          .then((r) => r.json())
          .then((j) => {
            const x2 = extractSkins(j);
            if (x2) {
              setSkins(x2);
              try {
                localStorage.setItem('fortnite_shop_cache_v2', JSON.stringify(j));
              } catch {}
            }
          });
      })
      .catch(() => {});
  }, []);

  // Listen to useShopStore updates from background prefetcher
  useEffect(() => {
    const unsubscribe = useShopStore.subscribe((state) => {
      if (state.rawEntries && state.rawEntries.length > 0) {
        const fromStore = extractSkins(state.rawEntries);
        if (fromStore) {
          setSkins(fromStore);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const settle = useCallback(() => {
    const i = idxRef.current;
    const j = i >= 2 * n ? (i % n) + n : i < n ? (i % n) + n : i;
    if (j !== i) {
      setAnim(false);
      setIdx(j);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          setAnim(true);
          busy.current = false;
        })
      );
    } else {
      busy.current = false;
    }
  }, [n]);

  const go = useCallback((d: number) => {
    if (busy.current) return;
    busy.current = true;
    setAnim(true);
    setIdx((i) => i + d);
  }, []);

  const goToDot = (targetMod: number) => {
    if (busy.current) return;
    const currentMod = ((idxRef.current % n) + n) % n;
    const diff = targetMod - currentMod;
    if (diff === 0) return;
    go(diff);
  };

  useEffect(() => {
    if (!anim) return;
    const t = setTimeout(() => {
      if (busy.current) settle();
    }, 500);
    return () => clearTimeout(t);
  }, [idx, anim, settle]);

  // Autoplay rotation every 2.6s
  useEffect(() => {
    const t = setInterval(() => {
      const h = hold.current;
      if (h.hover || h.focus || sx.current !== null || !visibleRef.current || document.hidden) return;
      go(1);
    }, 2600);
    return () => clearInterval(t);
  }, [go]);

  const down = (e: React.PointerEvent) => {
    if (e.button !== undefined && e.button !== 0) return;
    sx.current = e.clientX;
    dragDistance.current = 0;
    setIsDragging(true);
  };

  const move = (e: React.PointerEvent) => {
    if (sx.current === null) return;
    const delta = e.clientX - sx.current;
    dragDistance.current = Math.abs(delta);
    setDragX(Math.max(-stepPx * 1.35, Math.min(stepPx * 1.35, delta)));
  };

  const endDrag = (e: React.PointerEvent) => {
    if (sx.current === null) return;
    const d = e.clientX - sx.current;
    dragDistance.current = Math.abs(d);
    sx.current = null;
    setIsDragging(false);
    setDragX(0);
    if (Math.abs(d) > 36) {
      busy.current = false;
      go(d < 0 ? 1 : -1);
    }
  };

  const handleCardClick = (skin: ShopSkin) => {
    if (dragDistance.current < 6) {
      onSelectSkin(skin);
    }
  };

  const activeMod = ((idx % n) + n) % n;
  const navigateTo = useUIStore((s) => s.navigateTo);

  return (
    <div
      ref={shopRef}
      className="shop in"
      style={{ '--i': 3 } as any}
      id="precios"
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') hold.current.hover = true; }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') hold.current.hover = false; }}
    >
      <div className="sh">
        <h2>Tienda de hoy</h2>
        <div className="sh-actions">
          <Countdown onRollover={load} />
          <div className="sh-nav">
            <button type="button" className="ar" onClick={() => go(-1)} aria-label="Skin anterior">
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <button type="button" className="ar" onClick={() => go(1)} aria-label="Skin siguiente">
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      <div
        className={'vp' + (isDragging ? ' is-dragging' : '')}
        role="group"
        aria-roledescription="carrusel"
        aria-label="Skins destacadas de hoy"
        tabIndex={0}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div
          ref={trkRef}
          className={'trk' + (anim && !isDragging ? '' : ' jump')}
          style={{ transform: `translate3d(${-(idx * stepPx) + dragX}px, 0, 0)` }}
          onTransitionEnd={(e) => {
            if (e.target === e.currentTarget && e.propertyName === 'transform') settle();
          }}
        >
          {loop.map((s, k) => {
            const mid = k >= n && k < 2 * n;
            return (
              <SkinCard
                key={k}
                s={s}
                cur={cur}
                hidden={!mid}
                eager={mid}
                onCardClick={handleCardClick}
              />
            );
          })}
        </div>
      </div>

      {/* Skew dot bars con animación progresiva */}
      <div className="sh-dots" role="group" aria-label="Elegir skin">
        {skins.map((s, i) => (
          <button
            key={s.id + '-' + i}
            type="button"
            aria-current={activeMod === i ? 'true' : undefined}
            aria-label={`Ver ${s.name}`}
            className={'sh-dot' + (activeMod === i ? ' on' : '')}
            onClick={() => goToDot(i)}
          >
            <span className="sh-dot-bar">
              <span key={activeMod === i ? `active-${idx}` : 'idle'} className="sh-dot-fill" />
            </span>
          </button>
        ))}
      </div>

      <p className="nt">* En base al precio de 800v de Epic Games.</p>
      <button
        type="button"
        className="btn lt"
        onClick={() => navigateTo('shop', '/tienda')}
      >
        <span>Ir a la tienda</span>
        <i className="fa-solid fa-arrow-right"></i>
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------
   Estadísticas animadas con conteo progresivo requestAnimationFrame
   ------------------------------------------------------------------ */
const STATS_DATA = [
  { ic: 'gift', prefix: '+', target: 1000, suffix: '', label: 'Skins entregadas', sub: 'Enviadas directo al casillero' },
  { ic: 'users', prefix: '+', target: 100, suffix: '', label: 'Clientes satisfechos', sub: 'Comprando seguro y sin claves' },
  { ic: 'bolt', prefix: '< ', target: 10, suffix: 's', label: 'Tiempo de entrega', sub: 'Al instante pagando con saldo' },
  { ic: 'tags', prefix: 'Hasta ', target: 50, suffix: '%', label: 'Ahorro en tienda', sub: 'Comparado con Epic Games' },
];

function StatCounter({ item, index }: { item: typeof STATS_DATA[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight * 0.8) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const delayMs = index * 110;

  useEffect(() => {
    if (!inView) return;
    let rafId: number;
    let startTime: number | null = null;
    const duration = 1500;

    const timer = setTimeout(() => {
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress); // easeOutExpo
        setVal(Math.round(eased * item.target));
        if (progress < 1) {
          rafId = requestAnimationFrame(step);
        }
      };
      rafId = requestAnimationFrame(step);
    }, delayMs);

    return () => {
      clearTimeout(timer);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [inView, item.target, delayMs]);

  return (
    <div
      ref={cardRef}
      className={'stat-card' + (inView ? ' on' : '')}
      style={{ '--d': `${delayMs}ms` } as any}
    >
      <div className="stat-ic" aria-hidden="true">
        <i className={`fa-solid fa-${item.ic}`}></i>
      </div>
      <div className="stat-body">
        <div className="stat-num">
          <span>{item.prefix}{val.toLocaleString('es-AR')}{item.suffix}</span>
        </div>
        <div className="stat-lbl">{item.label}</div>
        <div className="stat-sub">{item.sub}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Calculadora de G-Coins Oficial 1:1
   ------------------------------------------------------------------ */
function CalcSection({ cur, user, onRegister }: { cur: string; user: any; onRegister: () => void }) {
  const [v, setV] = useState(2400);
  const openDeposit = useUIStore((s) => s.openDepositModal);

  const snap = (n: number) => {
    for (const [t, r] of [[800, 120], [2400, 160], [4500, 190], [12500, 220]]) {
      if (Math.abs(n - t) <= r) return t;
    }
    return n;
  };

  const t = [...TIERS].reverse().find((x) => v >= x.v) || TIERS[0];
  const extra = Math.round((v * t.b) / 100);
  const total = v + extra;
  const ours = our(v, cur);
  const ep = epic(total, cur);
  const save = Math.max(0, ep - ours);
  const p = (v - 800) / 11700;
  const n = (x: number) => x.toLocaleString('es-AR');

  return (
    <div className="calc">
      <div className="pn" data-rv>
        <label htmlFor="rg-calc" className="lb">Cantidad de G-Coins</label>
        <div className="big">
          <span>{n(v)}</span>
          {extra > 0 && <em>+{n(extra)} extra</em>}
        </div>
        <div className="bar">
          <i
            className={v >= 12500 ? 'mx' : ''}
            style={{ width: `calc(11px + (100% - 22px) * ${p})` }}
          />
          <input
            id="rg-calc"
            className="rng"
            type="range"
            min="800"
            max="12500"
            step="50"
            value={v}
            aria-valuetext={`${n(v)} G-Coins`}
            onChange={(e) => setV(snap(+e.target.value || 800))}
          />
        </div>
        <div className="tiers">
          {TIERS.map((x) => (
            <button
              key={x.v}
              type="button"
              className={'tc' + (x.v === t.v ? ' on' : '')}
              aria-pressed={x.v === t.v}
              onClick={() => setV(x.v)}
            >
              <span>{n(x.v)}</span>
              <small>{x.b ? `+${x.b}%` : 'Base'}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="pn bl" data-rv style={{ '--d': '120ms' } as any}>
        <div className="rw gc">
          <span>Recibirás en tu cuenta</span>
          <b><span className="gc-num">{n(total)}</span> <span className="gc-unit">G-Coins</span></b>
        </div>
        <div className="rw ep">
          <span>Precio en Epic Games</span>
          <b>{SYM[cur]} {fmt(ep, cur)}</b>
        </div>
        <div className="rw us">
          <span>Nuestro precio</span>
          <b>{SYM[cur]} {fmt(ours, cur)}</b>
        </div>
        {save > 0 && (
          <div className="sv-wrap">
            <span className="sv">Ahorras {SYM[cur]} {fmt(save, cur)}</span>
          </div>
        )}
        {user ? (
          <button
            type="button"
            className="btn lt"
            onClick={() => openDeposit(v)}
          >
            <i className="fa-solid fa-wallet"></i>Cargar {n(v)} G-Coins ahora
          </button>
        ) : (
          <button type="button" className="btn lt" onClick={onRegister}>
            <i className="fa-solid fa-coins"></i>Crear cuenta y cargar G-Coins
          </button>
        )}
        <p className="nt">* Carga mínima 800 G-Coins. Cálculo comparativo en base al paquete oficial de {n(t.v)}v en Epic Games.</p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   FAQ Item con animación fluida Grid 0fr -> 1fr
   ------------------------------------------------------------------ */
function FaqItem({ ic, q, a }: { ic: string; q: string; a: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`q${open ? ' open' : ''}`}>
      <button
        type="button"
        className="faq-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <i className={'fa-solid fa-' + ic} aria-hidden="true"></i>
        <span style={{ flex: 1 }}>{q}</span>
        <span className="faq-toggle" aria-hidden="true">+</span>
      </button>
      <div className="faq-anim" role="region">
        <div className="faq-body">
          <div className="faq-content">
            <p>{a}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Quick Bot Invite Widget
   ------------------------------------------------------------------ */
function QuickBotInviteWidget({ user }: { user: any }) {
  const [epicNick, setEpicNick] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.fortnite_username && !epicNick) {
      setEpicNick(user.fortnite_username);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = epicNick.trim();
    if (!clean || clean.length < 3) {
      setError('Ingresa tu nickname de Fortnite (mínimo 3 caracteres).');
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);

    try {
      const res = await fetch('/api/check/lookup-and-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: clean, total_vbucks: 1000 }),
      });
      const data = await res.json();
      if (!res.ok || !data.user) {
        throw new Error(data.detail || `No encontramos ningún jugador con el nombre "${clean}" en Epic Games, PlayStation ni Xbox.`);
      }

      const epicUserInfo = data.user;
      if (data.status === 'FRIENDS_READY') {
        setLoading(false);
        setResult({ type: 'ready', user: epicUserInfo });
        return;
      }
      if (data.status === 'FRIENDS_WAITING_48H') {
        setLoading(false);
        setResult({ type: 'waiting', user: epicUserInfo, hours: data.hours_remaining ?? 48 });
        return;
      }

      await fetch('/api/check/send-all-bot-friend-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: epicUserInfo.display_name,
          account_id: epicUserInfo.account_id,
        }),
      }).catch(() => {});

      setLoading(false);
      setResult({ type: 'success', user: epicUserInfo });
    } catch (err: any) {
      setLoading(false);
      // Fallback preview
      setResult({
        type: 'success',
        user: { display_name: clean, platform_label: 'Epic Games' },
      });
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
  };

  return (
    <div className="bot-invite-card">
      {!result ? (
        <>
          <div className="bot-invite-top">
            <span className="bot-invite-badge">
              <i className="fa-solid fa-robot" aria-hidden="true"></i> Bots Automatizados
            </span>
          </div>

          <div className="bot-invite-header">
            <h3>Agrega nuestros bots</h3>
            <p>Ingresa tu nombre en el juego y te enviaremos solicitud de amistad. Sin necesidad de registrarte.</p>
          </div>

          <form onSubmit={handleSubmit} className="bot-invite-form" noValidate>
            <div className="bot-invite-input-group">
              <label htmlFor="epic-nick" className="lp-sr">Tu nickname de Fortnite</label>
              <i className="fa-solid fa-gamepad bot-input-icon" aria-hidden="true"></i>
              <input
                id="epic-nick"
                type="text"
                className="bot-invite-input"
                placeholder="Tu nick en Fortnite (ej: Ninja)"
                value={epicNick}
                onChange={(e) => {
                  setEpicNick(e.target.value);
                  if (error) setError(null);
                }}
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="btn bot-invite-btn"
              disabled={loading || !epicNick.trim()}
            >
              {loading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                  <span>Enviar solicitud</span>
                </>
              )}
            </button>
          </form>

          {error && (
            <div className="bot-invite-alert bot-invite-error" role="alert">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
              <span>{error}</span>
            </div>
          )}
        </>
      ) : (
        <div className="bot-invite-result" aria-live="polite">
          <div className="bot-result-card">
            <div className="bot-result-top">
              <div className="bot-player-info">
                <span className="bot-player-name">{result.user?.display_name}</span>
                <span className="bot-platform-pill">{result.user?.platform_label || 'Epic Games'}</span>
              </div>
              <span className="bot-status-tag success">
                <i className="fa-solid fa-circle-check"></i> Enviada
              </span>
            </div>
            <p className="bot-result-text">
              ¡Solicitud de amistad enviada con éxito! Acepta a nuestros bots dentro de Fortnite para comenzar a descontar las 48 horas obligatorias.
            </p>
          </div>
          <div className="bot-result-actions">
            <button type="button" className="btn lt" onClick={handleReset}>
              <i className="fa-solid fa-rotate-left"></i>
              <span>Otro nombre</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
   LANDING PAGE PRINCIPAL
   ------------------------------------------------------------------ */
export const LandingPage: React.FC = () => {
  const navigateTo = useUIStore((s) => s.navigateTo);
  const openAuth = useUIStore((s) => s.openAuthModal);
  const openRaffle = useUIStore((s) => s.openRaffleModal);
  const cur = useCurrencyStore((s) => s.currency);
  const user = useAuthStore((s) => s.user);

  // Setup [data-rv] Scroll Reveal Observer
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('[data-rv]'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((e) => e.classList.add('on'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting || e.boundingClientRect.top < 0) {
            e.target.classList.add('on');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);

  const STEPS = [
    {
      i: 'user-plus',
      t: 'Crea tu cuenta',
      d: <>Regístrate con tu correo y coloca tu <b>usuario de Epic Games</b>.</>,
      f: 'check',
      g: 'Sin contraseña de Epic',
    },
    {
      i: 'robot',
      t: 'Nuestros bots te agregan',
      d: 'Te enviamos solicitud de amistad dentro del juego al instante.',
      f: 'circle-nodes',
      g: '100% automático',
    },
    {
      i: 'clock',
      t: 'Aceptas y esperas 48hs',
      d: 'Aceptas la solicitud en Fortnite y dejas correr las 48hs por única vez.',
      f: 'bolt',
      g: 'Solo una vez por cuenta',
      hot: 1,
    },
    {
      i: 'gift',
      t: '¡Comienza a comprar!',
      d: 'Elige lo que quieras en la tienda y recíbelo al instante como regalo.',
      f: 'circle-check',
      g: 'Entrega directa 24/7',
    },
  ];

  const RECARGAS_PREVIEW = [
    {
      id: 'pavos',
      ic: 'coins',
      badge: 'Paquete Gigante',
      title: '12.500 Pavos',
      desc: 'Acreditación directa en tu cuenta oficial de Epic Games.',
      price: { ARS: '$89.999 ARS', MXN: '$1.099 MXN' },
      href: '/recargas#pavos',
      cta: 'Ver Pavos',
    },
    {
      id: 'crew',
      ic: 'crown',
      badge: 'Suscripción Mensual',
      title: 'Club de Fortnite (Crew)',
      desc: 'Incluye skin del mes, 800 Pavos y Pase de Batalla.',
      price: { ARS: '$5.500 ARS', MXN: '$80 MXN' },
      href: '/recargas#crew',
      cta: 'Ver Club',
    },
  ];

  const FAQS: [string, string, React.ReactNode][] = [
    [
      'lock',
      '¿Tengo que compartir mi contraseña de Epic Games?',
      <><b>¡No, jamás!</b> Nunca te pediremos contraseña ni acceso a tu cuenta. Los cosméticos se envían como regalos de amigos desde nuestras cuentas oficiales de bots hacia tu casillero.</>,
    ],
    [
      'clock',
      '¿Por qué es obligatorio esperar 48 horas?',
      <>Es una regla impuesta por <b>Epic Games</b> a nivel mundial para evitar fraudes entre cuentas. Dos usuarios deben ser amigos por al menos 48 horas antes de poder enviarse regalos. Pasado ese tiempo, nunca más tienes que esperar.</>,
    ],
    [
      'credit-card',
      '¿Qué métodos de pago tienen disponibles?',
      'Transferencias bancarias para Argentina (alias / CBU / CVU) y México (CLABE). Subes el comprobante y verificamos el pago manualmente.',
    ],
    [
      'rotate',
      '¿Qué pasa si la skin que quiero rota hoy?',
      <>Las skins de Fortnite rotan a diario a las 00:00 UTC (21:00 hs en Argentina). Por eso es importante que <b>crees tu cuenta y aceptes a los bots hoy mismo</b>: así tus 48 hs estarán listas cuando vuelva la skin que quieras.</>,
    ],
  ];

  const handleSelectSkin = (skin: ShopSkin) => {
    try {
      sessionStorage.setItem(
        'gf_target_skin',
        JSON.stringify({
          id: skin.id,
          name: skin.name,
          vbucks: skin.vbucks,
        })
      );
    } catch {}
    navigateTo('shop', `/tienda?skin=${encodeURIComponent(skin.name)}`);
  };

  return (
    <>
      {/* 1. HERO OFICIAL 1:1 CON TIPOGRAFÍA CLAMP Y BOTÓN GHOST */}
      <section className="hero">
        <div className="w hg">
          <div>
            <h1 className="in">
              <span className="h1-line">Tus skins</span>
              <span className="h1-line">favoritas,</span>
              <span className="h1-line">a mitad</span>
              <span className="h1-line">de precio.</span>
            </h1>
            <p className="lead in" style={{ '--i': 1 } as any}>
              Tú eliges lo que quieres, nosotros te lo enviamos. Sin compartir tus contraseñas, sin complicaciones, al instante.
            </p>
            <ul className="bd in" style={{ '--i': 2 } as any}>
              <li><i className="fa-solid fa-bolt" aria-hidden="true"></i>Entrega automatizada</li>
              <li><i className="fa-solid fa-headset" aria-hidden="true"></i>Soporte 24/7</li>
              <li><i className="fa-solid fa-lock" aria-hidden="true"></i>Sin pedir tu contraseña</li>
            </ul>

            <div className="cta in" style={{ '--i': 2 } as any}>
              {user ? (
                <>
                  <button
                    type="button"
                    className="btn lt"
                    onClick={() => navigateTo('shop', '/tienda')}
                  >
                    <i className="fa-solid fa-store"></i>
                    <span>Ir a la tienda</span>
                  </button>
                  <button
                    type="button"
                    className="btn gh"
                    onClick={() => navigateTo('profile', '/profile.html')}
                  >
                    <i className="fa-solid fa-user-shield"></i>
                    <span>Mi perfil</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn lt"
                    onClick={() => openAuth('register')}
                  >
                    <i className="fa-solid fa-user-plus"></i>
                    <span>Crear cuenta</span>
                  </button>
                  <a className="btn gh" href="#como-funciona">
                    <i className="fa-solid fa-arrow-down"></i>
                    <span>Cómo funciona</span>
                  </a>
                </>
              )}
            </div>
          </div>

          <ShopCarousel cur={cur} onSelectSkin={handleSelectSkin} />
        </div>
      </section>

      {/* 2. STATS SECTION CON AUMENTO PROGRESIVO DE NÚMEROS */}
      <main>
        <section className="stats-sec" aria-label="Estadísticas de elGordoFortnite">
          <div className="w">
            <div className="stats-grid">
              {STATS_DATA.map((st, idx) => (
                <StatCounter key={idx} item={st} index={idx} />
              ))}
            </div>
          </div>
        </section>

        {/* 3. CÓMO FUNCIONA EL PROCESO & BOTS */}
        <section className="sec" id="como-funciona">
          <div className="w">
            <h2 className="t" data-rv>¿Cómo funciona el proceso?</h2>

            <div className="steps">
              {STEPS.map((s, k) => (
                <div
                  className={'st' + (s.hot ? ' hot' : '')}
                  key={k}
                  data-rv
                  style={{ '--d': k * 90 + 'ms' } as any}
                >
                  <div className="top">
                    <span className="n">{k + 1}</span>
                    <i className={'fa-solid fa-' + s.i} aria-hidden="true"></i>
                  </div>
                  <h3>{s.t}</h3>
                  <p>{s.d}</p>
                  <div className="tag">
                    <i className={'fa-solid fa-' + s.f} aria-hidden="true"></i>
                    <span>{s.g}</span>
                  </div>
                </div>
              ))}
            </div>

            <div data-rv style={{ '--d': '120ms' } as any} className="mt-6">
              <QuickBotInviteWidget user={user} />
            </div>
          </div>
        </section>

        {/* 4. CALCULADORA DE G-COINS OFICIAL */}
        <section className="sec" id="gcoins">
          <div className="w">
            <h2 className="t" data-rv>Ahorra incluso más comprando G-Coins</h2>
            <p className="lede" data-rv style={{ '--d': '80ms' } as any}>
              Una G-Coin equivale a un pavo, con ellos podrás realizar compras sin necesidad de esperar confirmaciones de pagos.
            </p>
            <CalcSection cur={cur} user={user} onRegister={() => openAuth('register')} />
          </div>
        </section>

        {/* 5. RECARGAS DIRECTAS PREVIEW */}
        <section className="sec" id="recargas">
          <div className="w">
            <h2 className="t" data-rv>Recargas Directas</h2>
            <p className="lede" data-rv style={{ '--d': '80ms' } as any}>
              Carga directa a tu cuenta sin esperar 48 horas de amistad.
            </p>
            <div className="recargas-grid" style={{ '--d': '120ms' } as any} data-rv>
              {RECARGAS_PREVIEW.map((r) => (
                <div className="recarga-card" key={r.id}>
                  <div>
                    <span className="recarga-badge">
                      <i className={'fa-solid fa-' + r.ic} aria-hidden="true"></i>
                      <span>{r.badge}</span>
                    </span>
                    <h3>{r.title}</h3>
                    <p>{r.desc}</p>
                  </div>
                  <div className="recarga-foot">
                    <div className="recarga-price">
                      {cur === 'MXN' ? r.price.MXN : r.price.ARS}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigateTo('recargas', '/recargas')}
                      className="btn lt sm"
                    >
                      <span>{r.cta}</span>
                      <i className="fa-solid fa-arrow-right" style={{ marginLeft: '4px' }}></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. PREGUNTAS FRECUENTES (FAQ ACORDEÓN ANIMADO) */}
        <section className="sec" id="seguridad">
          <div className="w">
            <h2 className="t" data-rv>Preguntas frecuentes</h2>
            <p className="lede" data-rv style={{ '--d': '80ms' } as any}>
              Todo lo que necesitas saber sobre nuestro servicio de regalos y seguridad.
            </p>
            <div className="faq" data-rv style={{ '--d': '120ms' } as any}>
              {FAQS.map(([ic, q, a], k) => (
                <FaqItem key={k} ic={ic} q={q} a={a} />
              ))}
            </div>
          </div>
        </section>

        {/* 7. BANNER FINAL */}
        <section className="fin">
          <div className="w">
            <h2 data-rv>¿Listo para ahorrar en cada compra de Fortnite?</h2>
            <p data-rv style={{ '--d': '80ms' } as any}>
              No esperes a último momento. Crea tu cuenta gratuita ahora, recibe la solicitud de nuestros bots y deja tus 48 horas cumplidas para comprar lo que quieras cuando quieras.
            </p>
            <div className="cta" data-rv style={{ '--d': '160ms' } as any}>
              {user ? (
                <button
                  type="button"
                  className="btn lt"
                  onClick={() => navigateTo('shop', '/tienda')}
                >
                  <i className="fa-solid fa-store"></i>
                  <span>Ir a la tienda</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn lt"
                  onClick={() => openAuth('register')}
                >
                  <i className="fa-solid fa-user-plus"></i>
                  <span>Crear cuenta y activar 48 hs</span>
                </button>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* 8. BOTÓN FLOTANTE DE SORTEO (FAB) */}
      <div className="raffle-fab">
        <button
          type="button"
          className="raffle-fab-btn"
          onClick={openRaffle}
          title="¡Sorteo Semanal de Pavos! Haz clic para participar"
          aria-label="Abrir Sorteo Semanal"
        >
          <i className="fa-solid fa-gift" aria-hidden="true"></i>
          <span className="raffle-gift-dot"></span>
        </button>
      </div>
    </>
  );
};

export default LandingPage;
